import { useMemo } from "react";

import { useAppSelector } from "@/store";
import { roles } from "@/roles";

/**
 * Resolved tenant/client context for a query or mutation.
 *
 * The reconciliation flow surfaced the canonical bug class this hook fixes:
 * a TD admin acting on a TRADEDIVE_CLIENT tenant has no separate
 * `selectedClient` — the tenant *is* the client, with `clientId === tenantId`
 * (see commit a280d66 which backfills that invariant). Screens that read
 * `selectedClient?._id` directly silently break for that scenario, either
 * by sending `clientId: undefined` (returns wrong scope) or by throwing
 * "No client selected" before any network call.
 *
 * Always derive scope through this hook instead of reading the redux slices
 * directly. The precedence below mirrors the established pattern at
 * `useUploadFile.ts:69`.
 */
export interface EffectiveClientScope {
  /**
   * Effective clientId for backend queries and mutations. Resolution order:
   *  1. TD admin acting on a TRADEDIVE_CLIENT tenant → the tenant's id
   *     (clientId === tenantId for that data class).
   *  2. A real `selectedClient` picked via the client picker.
   *  3. SP_CLIENT user → their own user id (backend stamps clientId =
   *     user['custom:userId']).
   *  4. TD_CLIENT user → their tenantId (TD_CLIENT records are stamped
   *     clientId === tenantId per commit a280d66).
   *  5. `undefined` — caller should treat as "no scope yet, gate the UI".
   */
  clientId: string | undefined;
  /**
   * Effective tenantId. Set explicitly for admins viewing a tenant; for
   * non-admins the backend derives this from the token, but we still
   * expose `user.tenantId` here for symmetry.
   */
  tenantId: string | undefined;
  /** Convenience: user is in TD_ADMIN role. */
  isTDAdmin: boolean;
  /**
   * True iff TD_ADMIN AND the currently-selected tenant is a
   * TRADEDIVE_CLIENT. The tenant doubles as the client in that case.
   */
  isTDAdminTCD: boolean;
  /**
   * True when the user has a client picker available AND they have not yet
   * selected anything that would scope the query (no `selectedClient`,
   * not a TCD tenant). Use as `skip:` for RTK queries that require client
   * scope.
   */
  shouldRequireClientSelection: boolean;
}

const ROLES_WITH_CLIENT_PICKER: string[] = [
  roles.SPOWNER,
  roles.SPPARTNER,
  roles.TDADMIN,
];

export const useEffectiveClientScope = (): EffectiveClientScope => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector(
    (state) => state.tenantManagement
  );

  const isTDAdmin = user?.userRole === roles.TDADMIN;
  const isTDAdminTCD =
    isTDAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";

  const clientId = useMemo<string | undefined>(() => {
    if (isTDAdminTCD) return selectedTenant?._id;
    if (selectedClient?._id) return selectedClient._id;
    // Non-admin client-account fallback. The two roles diverge in how
    // their data is stamped, so the fallback differs by role:
    //
    //   - SP_CLIENT: backend explicitly sets clientId = user['custom:userId']
    //     for that role (see middleware/authorization.ts → resolveTenantAndClient)
    //     so SP_CLIENT trade data has clientId === user._id. Use user._id.
    //
    //   - TD_CLIENT: per commit a280d66 ("Stamp clientId=tenantId for
    //     TD_CLIENT and backfill"), TD_CLIENT-owned records are stamped
    //     with clientId === tenantId. Returning user._id here would
    //     produce queries that don't match any TD_CLIENT data — every
    //     dashboardVisibility query goes empty. Use user.tenantId.
    if (user?.userRole === roles.SPCLIENT && user?._id) {
      return user._id;
    }
    if (user?.userRole === roles.TDCLIENT && user?.tenantId) {
      return user.tenantId;
    }
    return undefined;
  }, [
    isTDAdminTCD,
    selectedTenant?._id,
    selectedClient?._id,
    user?.userRole,
    user?._id,
    user?.tenantId,
  ]);

  const tenantId = useMemo<string | undefined>(() => {
    if (isTDAdmin) return selectedTenant?._id;
    return user?.tenantId;
  }, [isTDAdmin, selectedTenant?._id, user?.tenantId]);

  const canSelectClients = useMemo(
    () => ROLES_WITH_CLIENT_PICKER.includes(user?.userRole ?? ""),
    [user?.userRole]
  );

  const shouldRequireClientSelection = useMemo(
    () => canSelectClients && !clientId,
    [canSelectClients, clientId]
  );

  return {
    clientId,
    tenantId,
    isTDAdmin,
    isTDAdminTCD,
    shouldRequireClientSelection,
  };
};

export default useEffectiveClientScope;
